import { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { Route, Switch, useLocation, matchPath } from 'react-router-dom'
import { AppLayout } from '../../App/Layout/AppLayout'
import { FavoriteListings } from '../../Components/homeSearch/FavoriteListings/FavoriteListings'
import { HomeSearchFooter } from '../../Components/homeSearch/HomeSearchFooter/HomeSearchFooter'
import { ListingDetails } from '../../Components/homeSearch/ListingDetails/ListingDetails'
import Listings from '../../Components/homeSearch/Listings'
import Loading from '../../Components/shared/Loading/LoadingApp'
import { NotFound } from '../../routes/NotFound'
import SavedSearches from '../../Components/homeSearch/SavedSearches'
import { Box, SkipNavContent } from '@homebotapp/hb-react-component-catalog'
import { PasswordGate } from '../../Components/homeSearch/PasswordGate/PasswordGate'
import { setCurrentClient, fetchClientData, fetchClientHomes, setClientIsGuest } from '../../actions/client'
import { fetchProfile } from '../../actions/customerProfile'
import { toggleListingSearchModal } from '../../actions/listings'
import { selectHomebotListingsFeatureEnabled } from '../../store/selectors/customerProfile'
import { selectListingsSearchIsLoading } from '../../store/selectors/listings'
import endsWith from 'lodash/endsWith'
import { getCurrentPath } from '../../helpers/browser'
import { PATH_HELPERS } from '../../constants/navigation'
import { getClientIdFromBearerToken, hasCustomerToken } from '../../auth'
import { ClientEventName, ClientEventSource } from '../../api/gqlaxy/gql/generated/graphql'
import { ActiveOverlayContextProvider } from '../../Components/shared/Routing/OverlayContext'
import { useCreateClientEventOnView } from '../../hooks/useCreateClientEventOnView'
import { setIsPublic } from '../../store/slices/auth'
import { selectFilter } from 'src/store/selectors/filters'
import { CITY, COUNTY, NEIGHBOURHOOD, PLACE_ID, POSTAL_CODE, STATE, STREET } from '../../constants/listings'
import qs from 'qs'
import { camelizeKeys } from '../../helpers/utils'
import { useAppSelector } from '../../store/hooks'
import { selectMarketInterestZipcodes } from '../../store/selectors/buyerInfo'
import { selectClientFetchId, selectClientHomes } from '../../store/selectors/client'
import { useViewAllListingsUrl } from '../../hooks/listings'
import history from '../../history'
import { fetchBuyerInfoData, fetchMortgageRates } from '../../actions'
import { selectHomeData } from '../../store/selectors/home'
import { HomeSearchLandingPage } from '../preview/homeSearchLandingPage'
import { useRecoilState } from 'recoil'
import { currentSearchLocation } from '../../Components/homeSearch/GoogleAutoComplete/state/currentSearchLocation'

const SearchAppLayout = ({ children }) => {
  return (
    <Box id='search-app-layout' width='100%' height='100%' position='fixed'>
      {children}
    </Box>
  )
}

const SearchRoutes = ({ match, isPublic }) => {
  const { customerProfileId: customerProfileIdFromUrl } = match.params
  const dispatch = useDispatch()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const [matchOverride, setMatchOverride] = useState({ isExact: true })
  const loading = useSelector(selectListingsSearchIsLoading)
  const clientIdFromToken = getClientIdFromBearerToken()
  const filter = useSelector(selectFilter)

  const currentPath = useMemo(() => getCurrentPath(), [location.pathname])
  const isValidListingPath = useMemo(
    () =>
      !!matchPath(currentPath, {
        path: [
          PATH_HELPERS.listing.matchPath,
          PATH_HELPERS.listings.matchPath,
          PATH_HELPERS.publicListing.matchPath,
          PATH_HELPERS.publicListings.matchPath
        ]
      }),
    [currentPath]
  )

  const searchEnabled = useSelector(selectHomebotListingsFeatureEnabled)
  const hasPublicKey = useAppSelector(state => state.auth.hasPublicKey)
  const hasBypassParam = searchParams.has('bypass')
  const isAuthedToPublicHomeSearch = hasPublicKey || hasBypassParam
  const queryParams = camelizeKeys(
    qs.parse(window.location.search, {
      ignoreQueryPrefix: true
    })
  )
  const hasBoundingBoxQueryParams =
    !!queryParams?.topLeftLat &&
    !!queryParams?.bottomRightLat &&
    !!queryParams?.topLeftLong &&
    !!queryParams?.bottomRightLong

  const locationAlreadySet =
    !!queryParams?.[POSTAL_CODE] ||
    !!queryParams?.[STREET] ||
    !!queryParams?.[NEIGHBOURHOOD] ||
    !!queryParams?.[COUNTY] ||
    !!queryParams?.[CITY] ||
    !!queryParams?.[STATE] ||
    !!queryParams?.[PLACE_ID] ||
    hasBoundingBoxQueryParams

  const urlsToAvoidRedirects = url => {
    const urls = ['/listing/', 'saved-searches', 'favorite-listings']
    return urls.some(u => url.includes(u))
  }

  const home = useSelector(selectHomeData)
  const homes = useSelector(selectClientHomes)
  const clientId = useSelector(selectClientFetchId)
  const [searchLocation] = useRecoilState(currentSearchLocation)
  const clientZipcodes = useSelector(selectMarketInterestZipcodes)
  const listingsSearchUrl = useViewAllListingsUrl(
    searchLocation?.place_id
      ? { placeId: searchLocation.place_id }
      : {
          zipcode:
            clientZipcodes?.length > 0 ? clientZipcodes[0] : home?.address ? home.address.zip : homes?.[0]?.address?.zip
        }
  )

  useEffect(() => {
    !locationAlreadySet && !clientZipcodes && clientId && dispatch(fetchBuyerInfoData(clientId))
  }, [dispatch, clientId, clientZipcodes, locationAlreadySet])

  useEffect(() => {
    if (!locationAlreadySet && listingsSearchUrl && !urlsToAvoidRedirects(location.pathname)) {
      history.push(listingsSearchUrl)
    }
  }, [home, clientZipcodes, listingsSearchUrl, locationAlreadySet, location.pathname])

  useEffect(() => {
    if (
      !filter?.displayName &&
      (!loading || isPublic) &&
      !urlsToAvoidRedirects(location.pathname) &&
      !locationAlreadySet &&
      !home &&
      (!clientZipcodes || clientZipcodes.length === 0)
    ) {
      dispatch(toggleListingSearchModal(true))
    }
  }, [filter?.displayName, loading])

  const createViewedHomeSearchEvent = useCreateClientEventOnView({
    eventName: ClientEventName.BuyerViewedHomeSearchEvent
  })
  const createViewClientEvent = useCreateClientEventOnView({
    eventName: ClientEventName.View,
    eventSource: ClientEventSource.ListingsSearch
  })
  createViewedHomeSearchEvent()
  createViewClientEvent()

  useEffect(() => {
    if (hasCustomerToken()) {
      console.warn('Customer token detected')
    }
  }, [customerProfileIdFromUrl])
  // Hack to override match.isExact required by AppLayout
  // that is handed to OverlayMask to hide background and
  // header when the preferences panel is shown.
  // This is needed because we have one AppLayout that wraps
  // HomeSearch route based components.
  useEffect(() => {
    if (
      endsWith(location.pathname, 'listings/preferences') ||
      endsWith(location.pathname, 'photos') ||
      endsWith(location.pathname, 'map')
    ) {
      setMatchOverride({ isExact: false })
    } else {
      setMatchOverride({ isExact: true })
    }
  }, [location.pathname, setMatchOverride])

  useEffect(() => {
    if (customerProfileIdFromUrl) {
      dispatch(fetchProfile(customerProfileIdFromUrl))
    }
  }, [dispatch, customerProfileIdFromUrl])

  useEffect(() => {
    if (isPublic) {
      dispatch(setIsPublic(true))
    }
  }, [dispatch, isPublic])

  useEffect(() => {
    const derivedClientId = clientIdFromToken || clientId
    if (derivedClientId) {
      dispatch(setCurrentClient(derivedClientId))
      dispatch(fetchClientData(derivedClientId, customerProfileIdFromUrl))
      dispatch(fetchClientHomes(derivedClientId))
    } else {
      dispatch(setClientIsGuest(true))
    }
  }, [dispatch, clientIdFromToken, customerProfileIdFromUrl, clientId])

  // this is a filthy hack. It fixes an issue where we'd see a flash of content before the loader shows up
  // resulting in a jarring and jenky UX. This is because selectListingsSearchIsLoading is inititally
  // false, then toggles to true when the page loads, then toggles back to false
  // Remove this when we have a better solution for loading
  const [localLoading, setLocalLoading] = useState(true)
  useEffect(() => {
    setLocalLoading(false)
    dispatch(fetchMortgageRates())
  }, [])

  if (localLoading || (loading && !isPublic)) {
    return <Loading />
  }

  if (!searchEnabled && !isPublic) {
    return <NotFound fullHeight />
  }

  return (
    <AppLayout
      loading={false}
      match={{ ...matchOverride, params: {}, path: '', url: '' }}
      enableAgentDropdown
      hideOverlayMaskOverflow
    >
      <SkipNavContent scrollMarginTop='var(--digest-module-scroll-margin-top)' />
      {(isAuthedToPublicHomeSearch || !isPublic) && (
        <div>
          <ActiveOverlayContextProvider>
            <Switch>
              <Route
                path={`${match.url}/listings`}
                render={() => (
                  <SearchAppLayout>
                    <Listings />
                  </SearchAppLayout>
                )}
              />
              <Route path={`${match.url}/listing/:listingId`} component={ListingDetails} />
              <Route path={`${match.url}/saved-searches`} component={SavedSearches} />
              <Route path={`${match.url}/favorite-listings`} component={FavoriteListings} />
              <Route path={`${match.url}/`} component={Listings} />
              <Route path={`${match.url}/lead`} component={HomeSearchLandingPage} />
              <Route path='*' render={() => <NotFound fullHeight />} />
            </Switch>
          </ActiveOverlayContextProvider>
        </div>
      )}
      {!isAuthedToPublicHomeSearch && isPublic && (
        <ActiveOverlayContextProvider>
          <PasswordGate />
        </ActiveOverlayContextProvider>
      )}

      {!isValidListingPath && <HomeSearchFooter />}
    </AppLayout>
  )
}

SearchRoutes.propTypes = {
  match: PropTypes.shape({
    isExact: PropTypes.bool,
    params: PropTypes.object,
    path: PropTypes.string,
    url: PropTypes.string
  }),
  isPublic: PropTypes.bool
}

export default SearchRoutes
